import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  fr: {
    loading: "Vérification de ton accès…",
    unauthorized: "Accès refusé",
    unauthorizedMsg: "Ton lien d'accès n'est pas valide ou a expiré. Vérifie ton courriel ou contacte le support.",
    expired: "Accès expiré",
    expiredMsg: "Ta période de replay est terminée.",
    scheduled: "Diffusion programmée",
    scheduledMsg: "Le livestream démarrera bientôt. Cette page se mettra à jour automatiquement.",
    countdown: "Compte à rebours",
    days: "j",
    hours: "h",
    minutes: "min",
    seconds: "s",
    live: "EN DIRECT MAINTENANT",
    liveMsg: "Tu regardes en direct. Si la vidéo ne charge pas, rafraîchis la page.",
    ended: "Diffusion terminée",
    endedMsgReplay: "Le replay sera disponible bientôt.",
    endedMsgNoReplay: "Merci d'avoir suivi en direct. Replay non disponible pour cet accès.",
    replayLive: "Replay disponible",
    replayMsg: "Tu visionnes le replay. Disponible jusqu'au",
    replayExpired: "Replay terminé",
    replayExpiredMsg: "La période de replay de cet événement est terminée. Merci d'avoir été des nôtres !",
    backupBtn: "Problème de lecture ? Basculer sur la source de secours",
    primaryBtn: "Revenir à la source principale",
    backupOn: "Tu regardes la source de secours.",
    welcome: "Bienvenue",
    accessType: "Type d'accès",
    refreshBtn: "🔄 Rafraîchir",
    supportTitle: "Besoin d'aide ?",
    supportMsg: "Écris-moi à ralph646@hotmail.com avec ta référence d'accès.",
    backHome: "← Accueil",
    by: "Présenté par",
    org: "Institut Impact Élite Martial · KAIZEN System",
    accessRef: "Réf accès",
    sponsorBadge: "🏆 SPONSOR",
    paidBadge: "✅ ACCÈS PAYÉ",
    mediaBadge: "📰 MÉDIA",
    staffBadge: "⚙️ STAFF",
    complimentaryBadge: "🎁 COMPLIMENTAIRE",
    blockedTitle: "Accès depuis un autre appareil",
    blockedMsg: "Ton lien d'accès est déjà utilisé sur un autre appareil. Chaque achat donne droit à un seul visionnement simultané. Ferme l'autre appareil puis rafraîchis cette page.",
  },
  en: {
    loading: "Verifying your access…",
    unauthorized: "Access denied",
    unauthorizedMsg: "Your access link is invalid or has expired. Check your email or contact support.",
    expired: "Access expired",
    expiredMsg: "Your replay period has ended.",
    scheduled: "Broadcast scheduled",
    scheduledMsg: "The livestream will start soon. This page will refresh automatically.",
    countdown: "Countdown",
    days: "d",
    hours: "h",
    minutes: "min",
    seconds: "s",
    live: "LIVE NOW",
    liveMsg: "You're watching live. If video doesn't load, refresh the page.",
    ended: "Broadcast ended",
    endedMsgReplay: "Replay will be available soon.",
    endedMsgNoReplay: "Thanks for watching live. Replay not included with this access.",
    replayLive: "Replay available",
    replayMsg: "You're watching the replay. Available until",
    replayExpired: "Replay ended",
    replayExpiredMsg: "The replay window for this event has ended. Thanks for watching!",
    backupBtn: "Playback issue? Switch to backup source",
    primaryBtn: "Back to main source",
    backupOn: "You're watching the backup source.",
    welcome: "Welcome",
    accessType: "Access type",
    refreshBtn: "🔄 Refresh",
    supportTitle: "Need help?",
    supportMsg: "Email me at ralph646@hotmail.com with your access reference.",
    backHome: "← Home",
    by: "Presented by",
    org: "Institut Impact Élite Martial · KAIZEN System",
    accessRef: "Access ref",
    sponsorBadge: "🏆 SPONSOR",
    paidBadge: "✅ PAID ACCESS",
    mediaBadge: "📰 MEDIA",
    staffBadge: "⚙️ STAFF",
    complimentaryBadge: "🎁 COMPLIMENTARY",
    blockedTitle: "Access from another device",
    blockedMsg: "Your access link is already in use on another device. Each purchase allows one simultaneous viewing. Close the other device then refresh this page.",
  },
};

const STATE_COLORS = {
  scheduled: "border-blue-500/40 bg-blue-500/5",
  countdown: "border-blue-500/40 bg-blue-500/5",
  live: "border-red-500/50 bg-red-500/10",
  ended: "border-zinc-700 bg-zinc-900/30",
  replay_live: "border-emerald-500/40 bg-emerald-500/5",
  expired: "border-amber-500/40 bg-amber-500/5",
  unauthorized: "border-red-500/40 bg-red-500/5",
  blocked: "border-red-500/50 bg-red-500/10",
};

function getDeviceId() {
  let id = localStorage.getItem("kaizen_device_id");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("kaizen_device_id", id);
  }
  return id;
}

export default function LivestreamViewerPortal() {
  const { accessToken } = useParams();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get("lang") === "en" ? "en" : "fr";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  const [blocked, setBlocked] = useState(false);
  const t = T[lang];

  // Initial load + auto-refresh every 30s
  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/livestream/public/viewer/${accessToken}`);
        setData(r.data);
        setError(null);
      } catch (e) {
        if (e.response?.status === 403) {
          setError(e.response?.data?.detail === "revoked" ? "revoked" : "unauthorized");
        } else {
          setError("error");
        }
      }
    };
    load();
    const interval = setInterval(load, 20000);  // révocation effective en < 1 min
    return () => clearInterval(interval);
  }, [accessToken]);

  // V2 anti-partage : heartbeat toutes les 30s quand le visionnement est actif
  useEffect(() => {
    const watching = data?.authorized && (data.state === "live" || data.state === "replay_live");
    if (!watching) return;
    const beat = async () => {
      try {
        const r = await axios.post(`${API}/livestream/public/viewer/${accessToken}/heartbeat`, {
          device_id: getDeviceId(),
          screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
        });
        setBlocked(Boolean(r.data?.blocked));
      } catch (e) {
        /* silencieux — ne casse pas le visionnement si le heartbeat échoue */
      }
    };
    beat();
    const hb = setInterval(beat, 30000);
    return () => clearInterval(hb);
  }, [accessToken, data?.authorized, data?.state]);

  // Countdown ticker
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  if (error === "revoked") {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full border border-red-500/40 bg-red-500/5 rounded-2xl p-8 text-center" data-testid="revoked-screen">
          <div className="text-4xl mb-3">⛔</div>
          <h1 className="text-xl font-bold">{lang === "en" ? "Access revoked" : "Accès révoqué"}</h1>
          <p className="text-sm text-zinc-400 mt-2">{lang === "en" ? "The organizer has revoked this access. Playback has been stopped." : "L'organisateur a révoqué cet accès. La lecture a été arrêtée."}</p>
        </div>
      </div>
    );
  }
  if (error === "unauthorized") {
    return (
      <StateLayout color={STATE_COLORS.unauthorized} testid="state-unauthorized">
        <div className="text-5xl mb-4">🚫</div>
        <h1 className="text-3xl sm:text-4xl font-bold">{t.unauthorized}</h1>
        <p className="text-zinc-400 mt-3 max-w-md">{t.unauthorizedMsg}</p>
        <Link to="/" className="mt-6 inline-block text-amber-400 hover:underline text-sm">{t.backHome}</Link>
        <Support t={t} />
      </StateLayout>
    );
  }

  if (!data) return <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center" data-testid="portal-loading">{t.loading}</div>;

  if (blocked) {
    return (
      <StateLayout color={STATE_COLORS.blocked} testid="state-blocked">
        <div className="text-5xl mb-4">🔒</div>
        <h1 className="text-3xl sm:text-4xl font-bold">{t.blockedTitle}</h1>
        <p className="text-zinc-400 mt-3 max-w-md">{t.blockedMsg}</p>
        <Button onClick={() => window.location.reload()} variant="outline" size="sm" className="mt-6 text-xs" data-testid="blocked-refresh-btn">
          {t.refreshBtn}
        </Button>
        <Support t={t} />
      </StateLayout>
    );
  }

  if (!data.authorized && data.state === "expired") {
    return (
      <StateLayout color={STATE_COLORS.expired} testid="state-expired">
        <div className="text-5xl mb-4">⏰</div>
        <h1 className="text-3xl sm:text-4xl font-bold">{t.expired}</h1>
        <p className="text-zinc-400 mt-3 max-w-md">{t.expiredMsg}</p>
        <div className="text-zinc-500 text-xs mt-4">{t.welcome}, {data.customer_name}</div>
        <Support t={t} />
      </StateLayout>
    );
  }

  const { event, state, customer_name, access_type, video_source, backup_source, expires_at, replay_until } = data;
  const title = lang === "fr" ? event.title_fr : event.title_en;
  const accessBadge = {
    paid: t.paidBadge,
    sponsor: t.sponsorBadge,
    media: t.mediaBadge,
    staff: t.staffBadge,
    complimentary: t.complimentaryBadge,
  }[access_type] || access_type;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="viewer-portal">
      {/* Top bar */}
      <div className="border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
          <Link to="/" className="text-xs tracking-[0.3em] text-amber-400">KAIZEN SYSTEM</Link>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-500">{t.welcome}, </span>
            <span className="text-zinc-200 font-semibold">{customer_name}</span>
            <span className="text-[10px] border border-amber-500/40 text-amber-300 rounded-full px-2 py-0.5">{accessBadge}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Event title */}
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="portal-event-title">{title}</h1>

        {/* State-specific UI */}
        <div className="mt-6">
          {state === "scheduled" && <ScheduledState event={event} now={now} t={t} lang={lang} />}
          {state === "countdown" && <ScheduledState event={event} now={now} t={t} lang={lang} />}
          {state === "live" && <><ViewerBanner text={data.banner} /><LiveState source={video_source} backup={backup_source} cameras={data.cameras} t={t} /></>}
          {state === "ended" && <EndedState event={event} t={t} replayIncluded={data.replay_included} />}
          {state === "replay_live" && <><ViewerBanner text={data.banner} /><ReplayState source={video_source} backup={backup_source} cameras={data.cameras} expires_at={replay_until || expires_at} t={t} lang={lang} /></>}
          {state === "replay_expired" && (
            <div className={`border rounded-2xl p-8 ${STATE_COLORS.ended} text-center`} data-testid="state-replay-expired">
              <div className="text-5xl mb-4">⏱️</div>
              <h2 className="text-2xl font-bold">{t.replayExpired}</h2>
              <p className="text-zinc-400 mt-3">{t.replayExpiredMsg}</p>
            </div>
          )}
        </div>

        <Support t={t} />

        {/* Footer */}
        <div className="mt-12 text-center text-zinc-500 text-xs">
          <div>{t.by}</div>
          <div className="text-amber-400 mt-1 tracking-[0.2em] font-semibold">{t.org}</div>
        </div>
      </div>
    </div>
  );
}

function ScheduledState({ event, now, t, lang }) {
  const start = event.start_at ? new Date(event.start_at) : null;
  const diff = start ? Math.max(0, start.getTime() - now.getTime()) : 0;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return (
    <div className={`border rounded-2xl p-8 ${STATE_COLORS.scheduled} text-center`} data-testid="state-scheduled">
      <div className="text-[10px] tracking-[0.4em] text-blue-300 mb-2 flex items-center justify-center gap-2">
        <span className="w-2 h-2 rounded-full bg-blue-400"></span>
        {t.scheduled.toUpperCase()}
      </div>
      <p className="text-zinc-300 mb-6">{t.scheduledMsg}</p>
      <div className="text-[10px] tracking-[0.3em] text-zinc-500 mb-3">{t.countdown.toUpperCase()}</div>
      <div className="grid grid-cols-4 gap-2 max-w-md mx-auto" data-testid="countdown-timer">
        <CountdownBox value={days} label={t.days} />
        <CountdownBox value={hours} label={t.hours} />
        <CountdownBox value={minutes} label={t.minutes} />
        <CountdownBox value={seconds} label={t.seconds} />
      </div>
      <div className="mt-6 text-xs text-zinc-500">
        {start && start.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "full", timeStyle: "short" })}
      </div>
    </div>
  );
}

function CountdownBox({ value, label }) {
  return (
    <div className="border border-blue-500/30 rounded-xl p-3 bg-black/30">
      <div className="text-3xl font-bold tabular-nums">{String(value).padStart(2, "0")}</div>
      <div className="text-[10px] text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function SourceSwitch({ source, backup, useBackup, setUseBackup, t }) {
  if (!backup?.source_url) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="backup-source-block">
      <Button onClick={() => setUseBackup(!useBackup)} variant="outline" size="sm" className="text-xs border-amber-500/40 text-amber-300" data-testid="backup-source-btn">
        {useBackup ? t.primaryBtn : t.backupBtn}
      </Button>
      {useBackup && <span className="text-[11px] text-amber-300" data-testid="backup-on-msg">{t.backupOn}</span>}
    </div>
  );
}

export function ViewerBanner({ text }) {
  if (!text) return null;
  return <div className="bg-amber-400 text-black text-sm font-bold px-4 py-2 text-center animate-pulse" data-testid="viewer-banner">📣 {text}</div>;
}

function CameraSwitch({ cameras, cam, setCam }) {
  if (!cameras || cameras.length < 2) return null;
  return (
    <div className="px-4 py-2 flex flex-wrap gap-2 border-b border-zinc-800/60 bg-black/40" data-testid="camera-switch">
      {cameras.map((c) => <button key={c.id} onClick={() => setCam(c.id)} className={`text-xs px-3 py-1 rounded-full border ${cam === c.id ? "bg-amber-400 text-black border-amber-400 font-bold" : "border-zinc-700 text-zinc-300 hover:border-amber-400/60"}`} data-testid={`camera-${c.id}`}>📹 {c.label}</button>)}
    </div>
  );
}

function LiveState({ source, backup, cameras, t }) {
  const [useBackup, setUseBackup] = useState(false);
  const [cam, setCam] = useState(null);
  const camSrc = cameras?.find((c) => c.id === cam);
  const url = camSrc ? camSrc.source_url : useBackup && backup?.source_url ? backup.source_url : source?.source_url;
  return (
    <div data-testid="state-live">
      <div className={`border rounded-2xl ${STATE_COLORS.live} overflow-hidden`}>
        <div className="px-4 py-3 flex items-center gap-2 border-b border-red-500/20">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></span>
          <span className="text-[10px] tracking-[0.4em] text-red-300 font-bold">{t.live}</span>
        </div>
        <CameraSwitch cameras={cameras} cam={cam} setCam={setCam} />
        <div className="aspect-video bg-black">
          {url ? (
            <iframe
              src={url}
              title="Livestream"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="livestream-iframe"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-zinc-500 text-sm px-6 text-center" data-testid="live-source-missing">
              {source?.cloudflare_error || t.scheduledMsg}
            </div>
          )}
        </div>
      </div>
      <p className="mt-3 text-xs text-zinc-400">{t.liveMsg}</p>
      <SourceSwitch source={source} backup={backup} useBackup={useBackup} setUseBackup={setUseBackup} t={t} />
      <Button
        onClick={() => window.location.reload()}
        variant="outline"
        size="sm"
        className="mt-3 text-xs"
        data-testid="refresh-btn"
      >
        {t.refreshBtn}
      </Button>
    </div>
  );
}

function EndedState({ event, t, replayIncluded }) {
  return (
    <div className={`border rounded-2xl p-8 ${STATE_COLORS.ended} text-center`} data-testid="state-ended">
      <div className="text-5xl mb-4">🏁</div>
      <h2 className="text-2xl font-bold">{t.ended}</h2>
      <p className="text-zinc-400 mt-3">{replayIncluded ? t.endedMsgReplay : t.endedMsgNoReplay}</p>
    </div>
  );
}

function ReplayState({ source, backup, cameras, expires_at, t, lang }) {
  const exp = expires_at ? new Date(expires_at) : null;
  const [useBackup, setUseBackup] = useState(false);
  const [cam, setCam] = useState(null);
  const camSrc = cameras?.find((c) => c.id === cam);
  const url = camSrc ? camSrc.source_url : useBackup && backup?.source_url ? backup.source_url : source?.source_url;
  return (
    <div data-testid="state-replay">
      <div className={`border rounded-2xl ${STATE_COLORS.replay_live} overflow-hidden`}>
        <div className="px-4 py-3 flex items-center gap-2 border-b border-emerald-500/20">
          <span className="text-[10px] tracking-[0.4em] text-emerald-300 font-bold">▶ {t.replayLive.toUpperCase()}</span>
        </div>
        <CameraSwitch cameras={cameras} cam={cam} setCam={setCam} />
        <div className="aspect-video bg-black">
          {url ? (
            <iframe
              src={url}
              title="Replay"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
              data-testid="replay-iframe"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-zinc-500 text-sm px-6 text-center" data-testid="replay-source-missing">{source?.cloudflare_error || "—"}</div>
          )}
        </div>
      </div>
      <SourceSwitch source={source} backup={backup} useBackup={useBackup} setUseBackup={setUseBackup} t={t} />
      {exp && (
        <p className="mt-3 text-xs text-zinc-400">
          {t.replayMsg} <strong>{exp.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "medium", timeStyle: "short" })}</strong>
        </p>
      )}
    </div>
  );
}

function Support({ t }) {
  return (
    <div className="mt-6 border border-zinc-800 rounded-xl p-4 bg-zinc-950/40" data-testid="support-block">
      <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">⚙️ {t.supportTitle.toUpperCase()}</div>
      <div className="text-xs text-zinc-400">{t.supportMsg}</div>
    </div>
  );
}

function StateLayout({ children, color, testid }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4" data-testid={testid}>
      <div className={`border rounded-2xl p-10 text-center max-w-lg w-full ${color}`}>
        {children}
      </div>
    </div>
  );
}
